import {UserUITheme, UserUIThemeVariation} from 'yougile-model'
import {Storage} from '@client/storage'
import {Sync} from '@client/sync'
import {Tracking} from '@common/tracking'
import {LeftAndTopPanelTheme} from '@common/tracking-events'
import {BoardTabBg} from '@components/board/board-tab-bg'
import {globals} from '@extensions/globals-base'

type AutoThemeVariation = 'darker' | 'lighter'

const prevThemeVariationKey = 'prev-theme-variation'

const themeAutoVariationCache = new Map<string, AutoThemeVariation>()

export class ThemeUtils {
  static async upTheme(): Promise<void> {
    const profile = Sync.getProfile()
    const userId = globals.currentUserId
    const user = profile.getUser(userId)
    if (!user) {
      return
    }

    const {skin, theme: newTheme} = user
    if (newTheme !== globals.theme) {
      globals.setTheme(newTheme)
    }
    ThemeUtils.applyTheme(newTheme)

    const themeVariation = globals.themeVariation[newTheme] ?? 'auto'
    if (themeVariation === 'auto') {
      // Apply saved theme variation before calculation to prevent monochrome
      // theme blinking because of long auto variation async calculation
      const prevThemeVariation = ThemeUtils.getPreviousAutoThemeVariation()
      ThemeUtils.applyThemeVariation(newTheme, prevThemeVariation)

      // Calculate theme variation for user's skin
      const url = BoardTabBg.getSkinUrl(skin)
      const themeVariation = await ThemeUtils.getAutoThemeVariationForSkin(url)

      // Apply variation only if nothing has changed during a long async call above
      const user = profile.getUser(userId)
      if (globals.theme === newTheme && user?.skin === skin) {
        ThemeUtils.setPreviousAutoThemeVariation(themeVariation)
        ThemeUtils.applyThemeVariation(newTheme, themeVariation)
      }
    } else {
      ThemeUtils.applyThemeVariation(newTheme, themeVariation)
    }
  }

  private static getPreviousAutoThemeVariation(): AutoThemeVariation {
    const prevThemeVariation = Storage.getItem(prevThemeVariationKey)
    return (prevThemeVariation ?? 'darker') as AutoThemeVariation
  }

  private static setPreviousAutoThemeVariation(
    themeVariation: AutoThemeVariation
  ): void {
    Storage.setItem(prevThemeVariationKey, themeVariation)
  }

  private static applyTheme(theme: UserUITheme): void {
    const isDarkTheme = theme === 'dark'
    document.documentElement.classList.toggle('dark', isDarkTheme)
    document.documentElement.classList.toggle('light', !isDarkTheme)
  }

  private static applyThemeVariation(
    theme: UserUITheme,
    themeVariation: UserUIThemeVariation
  ): void {
    const isDarkTheme = theme === 'dark'
    let variationClass = ''
    if (themeVariation !== 'none') {
      variationClass = (isDarkTheme ? 'dark' : 'light') + '--' + themeVariation
    }
    document.documentElement.classList.remove(
      'dark--lighter',
      'dark--darker',
      'light--lighter',
      'light--darker'
    )
    variationClass && document.documentElement.classList.add(variationClass)
  }

  private static async getAutoThemeVariationForSkin(
    skinUrl: string
  ): Promise<AutoThemeVariation> {
    const cached = themeAutoVariationCache.get(skinUrl)
    if (cached) {
      return cached
    }

    return new Promise(resolve => {
      // Try to not block on image loading stall&error
      const hnd = setTimeout(() => resolve('darker'), 5000)

      const img = new Image()
      img.src = skinUrl
      img.onerror = () => {
        clearTimeout(hnd)

        resolve('darker')
      }
      img.onload = () => {
        clearTimeout(hnd)

        try {
          const canvas = document.createElement('canvas')
          const width = 32
          const height = 32
          canvas.width = width
          canvas.height = 2 * height
          const ctx = canvas.getContext('2d')
          if (!ctx) {
            return resolve('darker')
          }

          // Use 20% left&20% top of the image to check its lightness b/c these are the parts behind panels
          const cw = 0.2
          const ch = 0.2

          // Project 20% left part to the first square
          const sw1 = Math.floor(cw * img.naturalWidth)
          const sh1 = img.naturalHeight
          ctx.drawImage(img, 0, 0, sw1, sh1, 0, 0, width, height)

          // Project 20% top part to the second square below
          const sw2 = img.naturalWidth - sw1
          const sh2 = Math.floor(ch * img.naturalHeight)
          ctx.drawImage(img, sw1, 0, sw2, sh2, 0, height, width, height)

          // Byte data of both projected squares
          const bytes = ctx.getImageData(0, 0, width, 2 * height).data

          // Calculate an array of luminances (https://stackoverflow.com/a/596243)
          const ys = []
          for (let idx = 0; idx < bytes.length; idx += 4) {
            const r = bytes.at(idx) ?? 0
            const g = bytes.at(idx + 1) ?? 0
            const b = bytes.at(idx + 2) ?? 0
            const y = 0.299 * r + 0.587 * g + 0.114 * b
            ys.push(y)
          }

          const threshold = 150
          ys.sort((a, b) => a - b)
          const value = ys[Math.floor(ys.length / 4)]

          const variant = value > threshold ? 'lighter' : 'darker'
          themeAutoVariationCache.set(skinUrl, variant)
          resolve(variant)
        } catch (e) {
          return resolve('darker')
        }
      }
    })
  }

  public static trackThemeVariation(
    themeVariation: UserUIThemeVariation | undefined,
    sendOnce?: boolean
  ): void {
    if (!themeVariation) {
      themeVariation = globals.themeVariation?.[globals.theme] ?? 'auto'
    }
    let leftAndTopPanelTheme: LeftAndTopPanelTheme = 'adaptiveAuto'
    if (themeVariation === 'none') {
      leftAndTopPanelTheme = 'gray'
    } else if (themeVariation === 'lighter') {
      leftAndTopPanelTheme = 'adaptiveLight'
    } else if (themeVariation === 'darker') {
      leftAndTopPanelTheme = 'adaptiveDark'
    }
    void Tracking.sendUserProperty({leftAndTopPanelTheme}, sendOnce)
  }
}
